import { Bath, Bed, Square, MapPin, Heart, ArrowUpRight, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CarouselApi } from "@/components/ui/carousel";

type CardListing = {
  id: string;
  isExternal: boolean;
  price: number | null;
  address: string | null;
  city?: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  image: string | null;
};

const Listings = () => {
  const [listings, setListings] = useState<CardListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [arrowsVisible, setArrowsVisible] = useState(false);
  const arrowsTimer = useRef<number | null>(null);

  const showArrows = () => {
    setArrowsVisible(true);
    if (arrowsTimer.current) window.clearTimeout(arrowsTimer.current);
    arrowsTimer.current = window.setTimeout(() => setArrowsVisible(false), 2000);
  };

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      setSelectedIdx(api.selectedScrollSnap());
      showArrows();
    };
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [internalRes, externalRes] = await Promise.all([
          supabase
            .from("listings")
            .select("id, price, address, city, bedrooms, bathrooms, square_feet, photo_url")
            .eq("status", "approved")
            .not("price", "is", null)
            .gt("price", 0)
            .order("created_at", { ascending: false })
            .limit(12),
          supabase
            .from("external_listings")
            .select("id, price, address, city, bedrooms, bathrooms, square_feet, photos")
            .eq("is_active", true)
            .not("price", "is", null)
            .gt("price", 0)
            .order("created_at", { ascending: false })
            .limit(18),
        ]);

        const internal: CardListing[] = (internalRes.data || [])
          .filter((l: any) => l.address && l.bedrooms && l.bathrooms)
          .map((l: any) => ({
            id: l.id,
            isExternal: false,
            price: l.price,
            address: l.address,
            city: l.city,
            bedrooms: l.bedrooms,
            bathrooms: l.bathrooms,
            square_feet: l.square_feet,
            image: l.photo_url,
          }));

        const external: CardListing[] = (externalRes.data || [])
          .filter((l: any) => l.address && l.bedrooms && l.bathrooms)
          .map((l: any) => ({
            id: l.id,
            isExternal: true,
            price: l.price,
            address: l.address,
            city: l.city,
            bedrooms: l.bedrooms,
            bathrooms: l.bathrooms,
            square_feet: l.square_feet,
            image: Array.isArray(l.photos) && l.photos.length > 0 ? l.photos[0] : null,
          }));

        // Interleave internal first, then external — cap at 12
        const combined = [...internal, ...external].slice(0, 10);
        setListings(combined);
      } catch (err) {
        console.error("Error fetching listings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  return (
    <section className="pt-2 pb-20 md:py-36 bg-canvas">
      <div className="max-w-[1200px] mx-auto px-8 md:px-12">
        {/* Editorial header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-16">
          <div>
            <div className="inline-flex items-center gap-2.5 mb-6">
              <span className="w-8 h-px bg-gold-deep" />
              <span className="text-[11px] font-medium tracking-[0.25em] uppercase text-ink-soft">
                The catalog
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl font-light text-ink leading-[1.0] tracking-tight">
              Shop homes,
              <br />
              <span className="accent-italic text-wood-deep">not commissions.</span>
            </h2>
          </div>
          <Link
            to="/listings"
            className="link-gold text-ink font-medium text-sm tracking-wide self-start md:self-end pb-1 inline-flex items-center gap-1"
          >
            View all listings
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-[16px] overflow-hidden shadow-soft">
                <Skeleton className="w-full h-72" />
                <div className="p-6 space-y-3">
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : listings.length > 0 ? (
          <Carousel
            opts={{ align: "start", loop: false, dragFree: false }}
            setApi={setApi}
            className="w-full"
            id="listings-carousel"
            onTouchStart={showArrows}
            onPointerDown={showArrows}
            onMouseEnter={showArrows}
          >
            <CarouselContent className="-ml-4 md:-ml-6">
              {listings.map((property, idx) => (
                <CarouselItem
                  key={`${property.isExternal ? "ext" : "int"}-${property.id}`}
                  className="pl-4 md:pl-6 basis-[85%] sm:basis-[60%] md:basis-1/2 lg:basis-1/3"
                >
                  <Link
                    to={`/listings/${property.id}`}
                    className="group card-lift bg-card rounded-[16px] overflow-hidden shadow-soft block reveal-up h-full"
                    style={{ animationDelay: `${idx * 0.06}s` }}
                  >
                    <div className="relative overflow-hidden aspect-[4/3] bg-canvas-deep">
                      <img
                        src={
                          property.image ||
                          "https://images.unsplash.com/photo-1721322800607-8c38375eef04?w=600&h=450&fit=crop"
                        }
                        alt={property.address || "Home"}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/35 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                      <div className="absolute top-5 left-5">
                        <span className="bg-canvas/95 backdrop-blur-sm text-ink text-[10px] font-medium uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
                          Available
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="absolute top-5 right-5 w-9 h-9 rounded-full bg-canvas/95 backdrop-blur-sm flex items-center justify-center hover:bg-canvas transition-all duration-500 hover:scale-110"
                        aria-label="Save listing"
                      >
                        <Heart
                          className="w-4 h-4 text-ink group-hover:text-gold-deep transition-colors duration-500"
                          strokeWidth={1.5}
                        />
                      </button>

                      <div className="absolute bottom-5 left-5 right-5 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-700">
                        <div className="bg-canvas text-ink text-center text-[11px] font-medium tracking-[0.18em] uppercase py-3 rounded-md">
                          Quick view →
                        </div>
                      </div>
                    </div>

                    <div className="p-7">
                      <div className="flex items-baseline justify-between mb-3">
                        <h3 className="font-display text-3xl font-light text-ink tabular-nums tracking-tight">
                          ${property.price?.toLocaleString()}
                        </h3>
                      </div>
                      <div className="flex items-center gap-1.5 mb-5 text-ink-soft text-sm font-light">
                        <MapPin className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                        <span className="truncate">{property.address}</span>
                      </div>
                      <div className="flex items-center gap-5 text-ink-soft text-xs pt-4 border-t border-ink/8">
                        <div className="flex items-center gap-1.5">
                          <Bed className="h-3.5 w-3.5" strokeWidth={1.5} />
                          <span className="font-medium text-ink">{property.bedrooms}</span> bd
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Bath className="h-3.5 w-3.5" strokeWidth={1.5} />
                          <span className="font-medium text-ink">{property.bathrooms}</span> ba
                        </div>
                        {property.square_feet ? (
                          <div className="flex items-center gap-1.5">
                            <Square className="h-3.5 w-3.5" strokeWidth={1.5} />
                            <span className="font-medium text-ink">
                              {property.square_feet?.toLocaleString()}
                            </span>{" "}
                            sf
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4 bg-canvas border-ink/15 text-ink hover:bg-ink hover:text-canvas" />
            <CarouselNext className="hidden md:flex -right-4 bg-canvas border-ink/15 text-ink hover:bg-ink hover:text-canvas" />
            {/* Mobile: floating scroll arrows — appear on interaction */}
            <button
              type="button"
              onClick={() => { showArrows(); api?.scrollPrev(); }}
              aria-label="Scroll to previous home"
              className={`md:hidden absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-canvas/95 backdrop-blur-sm border border-ink/15 shadow-soft flex items-center justify-center text-ink transition-opacity duration-300 ${arrowsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => { showArrows(); api?.scrollNext(); }}
              aria-label="Scroll to next home"
              className={`md:hidden absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-canvas/95 backdrop-blur-sm border border-ink/15 shadow-soft flex items-center justify-center text-ink transition-opacity duration-300 ${arrowsVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            {/* Dot indicators (mobile + desktop) */}
            <div className="flex justify-center gap-1.5 mt-6 md:mt-8">
              {listings.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to listing ${i + 1}`}
                  onClick={() => api?.scrollTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === selectedIdx ? "w-6 bg-ink" : "w-1.5 bg-ink/25"
                  }`}
                />
              ))}
            </div>
          </Carousel>
        ) : (
          <div className="text-center py-16 bg-card rounded-[16px] shadow-soft">
            <p className="text-ink-soft mb-6 font-light">No properties available yet.</p>
            <Button
              asChild
              className="bg-gold hover:bg-gold-deep text-ink font-medium rounded-md px-7 py-6 transition-all duration-500"
            >
              <Link to="/list-property">Be the first to list</Link>
            </Button>
          </div>
        )}

        {/* CTA — view more */}
        {listings.length > 0 && (
          <div className="mt-16 flex flex-col items-center text-center">
            <p className="text-sm text-ink-soft font-light mb-5 max-w-md">
              Browse the full catalog of homes across Central Florida.
            </p>
            <Link to="/listings">
              <Button
                size="lg"
                className="group bg-ink hover:bg-ink/90 text-canvas font-medium px-8 py-6 text-sm tracking-wide rounded-md transition-all duration-500"
              >
                View all listings
                <ArrowUpRight className="ml-2 w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Listings;
